import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  FieldErrors,
  fieldErrorsForPrefix as valErrFieldErrorsForPrefix,
} from "@/shared/lib/errors/validation-error";
import { isFetchBaseQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { ErrDocumentNotReady } from "@bitory-gmbh/bsm";
import { ErrValidationFailed } from "@bitory-gmbh/nidavellir";

export function useErrDocumentNotReady() {
  return useContext(ErrDocumentNotReadyContext);
}

/**
 * useSetErrDocumentNotReadyFromRtk is a convenience hook to
 * set the document not ready error. It saves the need to
 * write a useEffect hook and related boilerplate code.
 * The cleanUp function is optional and will be called
 * if the error is set.
 *
 * @param error - The error to check and set.
 * @param cleanUp - A function that will be called if the error is set.
 */
export function useSetErrDocumentNotReadyFromRtk(
  error: unknown,
  cleanUp?: () => void,
) {
  const { setNotReadyError } = useErrDocumentNotReady();

  useEffect(() => {
    // attempt to retrieve the error from the rtk error
    const err = fromRtkErr(error);
    if (!err) {
      return;
    }

    setNotReadyError(err);
    if (cleanUp) {
      cleanUp();
    }
  }, [error, cleanUp, setNotReadyError]);
}

interface ErrDocumentNotReadyContextInterface {
  notReadyError: ErrDocumentNotReady | null;
  setNotReadyError: (error: ErrDocumentNotReady | null) => void;
  resetNotReadyErrorField: (field: string) => void;
  resetNotReadyError: () => void;
}

const ErrDocumentNotReadyContext =
  createContext<ErrDocumentNotReadyContextInterface>({
    notReadyError: null,
    setNotReadyError: () => null,
    resetNotReadyErrorField: () => () => null,
    resetNotReadyError: () => null,
  });

export function ErrDocumentNotReadyContextProvider({
  children,
}: PropsWithChildren) {
  const [notReadyError, setNotReadyError] =
    useState<ErrDocumentNotReady | null>(null);

  const value: ErrDocumentNotReadyContextInterface = useMemo(() => {
    const setNotReadyErrorWithCheck = (error: unknown) => {
      if (!error) {
        return;
      }

      if (!isErrDocumentNotReady(error)) {
        return;
      }

      setNotReadyError(error);
    };

    const resetNotReadyErrorField = (field: string) => {
      if (!notReadyError) {
        return;
      }

      const newError = removeErrorsFor(notReadyError, field);

      // if the last field error was removed, reset the error to null
      if (Object.entries(newError.errors).length === 0) {
        setNotReadyError(null);
        return;
      }

      setNotReadyError(newError);
    };

    const resetNotReadyError = () => {
      setNotReadyError(null);
    };

    return {
      notReadyError,
      setNotReadyError: setNotReadyErrorWithCheck,
      resetNotReadyErrorField,
      resetNotReadyError,
    };
  }, [notReadyError, setNotReadyError]);

  return (
    <ErrDocumentNotReadyContext.Provider value={value}>
      {children}
    </ErrDocumentNotReadyContext.Provider>
  );
}

function fromRtkErr(error: unknown): ErrDocumentNotReady | null {
  if (!isFetchBaseQueryError(error)) {
    return null;
  }

  if (error.status !== 400) {
    return null;
  }

  if (!isErrDocumentNotReady(error.data)) {
    return null;
  }

  return error.data;
}

function isErrDocumentNotReady(error: unknown): error is ErrDocumentNotReady {
  if (!error || typeof error !== "object") {
    return false;
  }

  if (!("error" in error)) {
    return false;
  }

  // We rely on the backend to serve the correct type.
  // This may break the portal if it changes, but
  // failing fast should be preferred here.
  if (error.error === "bsm.ErrDocumentNotReady") {
    return true;
  }

  return false;
}

function removeErrorsFor(
  error: ErrDocumentNotReady,
  field: string,
): ErrDocumentNotReady {
  return {
    ...error,
    errors: Object.fromEntries(
      Object.entries(error.errors).filter(([key]) => key !== field),
    ),
  };
}

/**
 * A helper function to adjust UI behaviour based on the error.
 * @param error The error to check.
 */
export function rtkErrIsErrDocumentNotReady(
  error: unknown,
): error is { data: ErrDocumentNotReady } {
  if (!error) {
    return false;
  }

  if (!isFetchBaseQueryError(error)) {
    return false;
  }

  if (error.status !== 400) {
    return false;
  }

  return isErrDocumentNotReady(error.data);
}

export function resetField(
  error: unknown,
  field: string,
): ErrDocumentNotReady | null {
  if (!error) {
    return null;
  }

  if (!isErrDocumentNotReady(error)) {
    return null;
  }

  const entries = Object.entries(error.errors).filter(([key]) => key !== field);

  // if there are no errors left, delete whole error
  if (entries.length === 0) {
    return null;
  }

  const errors = Object.fromEntries(entries);

  return {
    ...error,
    errors,
  };
}

export function errorsFor(
  error: ErrValidationFailed | null,
  field: string,
): string[] | null {
  return error?.errors[field] ?? null;
}

export function hasErrors(
  error: ErrDocumentNotReady | null,
  field: string,
): boolean {
  if (!error) {
    return false;
  }

  return error?.errors[field] !== undefined;
}

export function fieldErrorsForPrefix(
  error: ErrDocumentNotReady | null,
  prefix: string,
): FieldErrors | undefined {
  return valErrFieldErrorsForPrefix(error, prefix);
}
